import type { DiagnosticReport, Observation } from '@medplum/fhirtypes';
import { useTranslation } from 'react-i18next';
import { Table, TableBody, TableHead, TableHeader, TableRow } from '../ui/table';
import { DiagnosticReportHeader } from './DiagnosticReportHeader';
import { DiagnosticReportObservationRows } from './DiagnosticReportObservationRows';

export type DiagnosticReportDisplayProps = {
  diagnosticReport: DiagnosticReport;
  /**
   * Resolved Observations referenced from `DiagnosticReport.result[]` and (recursively) from
   * each Observation's `hasMember[]`. Keyed by Observation id. The orchestrator is responsible
   * for walking refs and populating this map.
   */
  observations: Map<string, Observation>;
  /** When provided, each result row becomes clickable and calls this with the row's Observation. */
  onSelectObservation?: (observation: Observation) => void;
};

/**
 * Renders a `DiagnosticReport` and its referenced `Observation`s as the lab-results table —
 * header strip with subject/performer/issued/status, the results table itself with recursive
 * panel grouping via `Observation.hasMember`, and the report's `conclusion` text below.
 *
 * Pure presentational. The orchestrator is responsible for fetching the DR and the full set
 * of referenced Observations (including recursive `hasMember` walking) and passing them in.
 */
export const DiagnosticReportDisplay = ({
  diagnosticReport,
  observations,
  onSelectObservation,
}: DiagnosticReportDisplayProps): React.ReactElement => {
  const { t } = useTranslation('records');
  const resultRefs = diagnosticReport.result ?? [];

  return (
    <div className="space-y-4">
      <DiagnosticReportHeader diagnosticReport={diagnosticReport} />

      {resultRefs.length > 0 && (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('diagnosticReportTest')}</TableHead>
              <TableHead>{t('diagnosticReportValue')}</TableHead>
              <TableHead>{t('diagnosticReportReferenceRange')}</TableHead>
              <TableHead>{t('diagnosticReportInterpretation')}</TableHead>
              <TableHead>{t('diagnosticReportCategory')}</TableHead>
              <TableHead>{t('diagnosticReportPerformer')}</TableHead>
              <TableHead>{t('diagnosticReportStatus')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            <DiagnosticReportObservationRows
              refs={resultRefs}
              observations={observations}
              ancestorIds={new Set()}
              onSelectObservation={onSelectObservation}
            />
          </TableBody>
        </Table>
      )}

      {diagnosticReport.conclusion && (
        <div className="space-y-1">
          <div className="text-xs font-medium text-muted-foreground uppercase">{t('diagnosticReportConclusion')}</div>
          <p className="text-sm whitespace-pre-wrap">{diagnosticReport.conclusion}</p>
        </div>
      )}
    </div>
  );
};
